import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Receipt, MagnifyingGlass, ArrowsClockwise, Spinner,
  ArrowSquareOut, CalendarBlank, FileText, FunnelSimple, X,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import { HelpPill } from "@/components/HeaderBrand";
import { useT } from "@/lib/i18n";

/**
 * MyPreparedQuotesPage — every Quote_Heading row where
 * ``Quote Prepared By`` matches the signed-in user's email. Backed by
 * ``GET /api/me/prepared-quotes``. The backend gates the endpoint to
 * UsAir staff (anyone with a row in ``UsAirPeople``); the frontend
 * surfaces a clean 403 message when the API refuses.
 *
 * Features:
 *  • Status & Q_Type filter chips (multi-select).
 *  • Free-text search (heading id / company id / company name / invoice #).
 *  • Optional date range (Quote Date).
 *  • Pagination with total count.
 *  • Summary bar: counts by status, grand total, open balance.
 *  • Click any row → opens the quote / invoice detail page.
 */
export default function MyPreparedQuotesPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(new Set()); // Set<string>
  const [typeFilter, setTypeFilter] = useState(new Set());     // Set<string>
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 50;

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = { page, per_page: perPage };
      if (search.trim()) params.search = search.trim();
      if (statusFilter.size) params.status = Array.from(statusFilter).join(",");
      if (typeFilter.size)   params.q_type = Array.from(typeFilter).join(",");
      if (fromDate) params.from_date = fromDate;
      if (toDate)   params.to_date = toDate;
      const r = await api.get("/me/prepared-quotes", { params });
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Could not load quotes";
      setErr(detail);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, typeFilter, fromDate, toDate, page]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 whenever a filter changes
  useEffect(() => { setPage(1); }, [search, statusFilter, typeFilter, fromDate, toDate]);

  const items = data?.items || [];
  const total = data?.total || 0;
  const summary = data?.summary || { by_status: {}, by_type: {}, grand_total: 0, open_balance: 0 };
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const fmtMoney = (n, ccy = "USD") => {
    const sign = (Number(n) || 0) < 0 ? "-" : "";
    return `${sign}${ccy === "CAD" ? "CA$" : "$"}${Math.abs(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return String(d); }
  };

  const toggleSet = (set, setter) => (value) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilter(new Set());
    setTypeFilter(new Set());
    setFromDate("");
    setToDate("");
  };

  const hasAnyFilter = useMemo(
    () => Boolean(search.trim() || statusFilter.size || typeFilter.size || fromDate || toDate),
    [search, statusFilter, typeFilter, fromDate, toDate],
  );

  const statusKeys  = Object.keys(summary.by_status || {});
  const typeKeys    = Object.keys(summary.by_type || {});

  const statusColor = (s) => {
    const k = (s || "").toLowerCase();
    if (k === "paid") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (k === "preliminary") return "bg-slate-50 text-slate-600 border-slate-200";
    if (k === "quote") return "bg-blue-50 text-blue-700 border-blue-200";
    if (k === "pending approval") return "bg-amber-50 text-amber-700 border-amber-200";
    if (k === "approved") return "bg-violet-50 text-violet-700 border-violet-200";
    if (k === "sold & order confirmed") return "bg-teal-50 text-teal-700 border-teal-200";
    if (k === "cancelled") return "bg-rose-50 text-rose-700 border-rose-200";
    if (k === "warranty") return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-slate-50 text-slate-600 border-slate-200";
  };

  return (
    <div className="min-h-screen bg-white" data-testid="my-prepared-quotes-page">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            data-testid="my-quotes-back-btn"
            className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
            title={t("action.back", "Back")}>
            <ArrowLeft size={18} weight="bold" />
          </button>
          <Receipt size={20} weight="fill" className="text-blue-700" />
          <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo']" data-testid="my-quotes-heading">
            {t("my_prepared_quotes.title", "Quotes Prepared by Me")}
          </h1>
          {data && (
            <span className="text-xs text-slate-500 font-mono ml-1" data-testid="my-quotes-total-pill">
              {total.toLocaleString()} {total === 1 ? t("my_prepared_quotes.row_singular", "row") : t("my_prepared_quotes.row_plural", "rows")}
            </span>
          )}
          <button onClick={load} disabled={loading}
            data-testid="my-quotes-reload-btn"
            className={`ml-2 w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 ${loading ? "text-blue-600" : "text-slate-500"}`}
            title={loading ? "Reloading…" : "Reload"}>
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
          <div className="ml-auto flex items-center gap-2">
            <HelpPill />
            <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          </div>
        </div>

        {/* Filter bar */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-3 flex flex-wrap items-center gap-2">
          <div className="relative max-w-md flex-1 min-w-[180px]">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="my-quotes-search"
              placeholder={t("my_prepared_quotes.search_placeholder", "Search company, heading, invoice #…")}
              className="pl-9 h-9 rounded-sm border-slate-300 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-500">
            <CalendarBlank size={12} weight="bold" />
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              data-testid="my-quotes-from-date"
              className="h-7 px-2 rounded-sm border border-slate-300 bg-white text-[11px]"
              title={t("my_prepared_quotes.from_date_tooltip", "From date (Quote Date)")} />
            <span className="text-slate-400">→</span>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              data-testid="my-quotes-to-date"
              className="h-7 px-2 rounded-sm border border-slate-300 bg-white text-[11px]"
              title={t("my_prepared_quotes.to_date_tooltip", "To date (Quote Date)")} />
          </div>
          {hasAnyFilter && (
            <button onClick={clearAllFilters}
              data-testid="my-quotes-clear-filters"
              className="inline-flex items-center gap-1 h-7 px-2 rounded-sm border border-slate-300 hover:bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-600">
              <X size={11} weight="bold" /> {t("my_prepared_quotes.clear_filters", "Clear")}
            </button>
          )}
        </div>

        {/* Filter chips: status + type */}
        {(statusKeys.length > 0 || typeKeys.length > 0) && (
          <div className="max-w-7xl mx-auto px-4 md:px-8 pb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px]">
            {statusKeys.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="my-quotes-status-chips">
                <FunnelSimple size={12} weight="bold" className="text-slate-400" />
                <span className="font-bold uppercase tracking-wider text-slate-500 mr-1">{t("my_prepared_quotes.filter_status", "Status")}</span>
                {statusKeys.map((s) => {
                  const active = statusFilter.has(s);
                  const meta = summary.by_status[s] || { count: 0, total: 0 };
                  return (
                    <button key={s} type="button"
                      data-testid={`my-quotes-status-chip-${s}`}
                      onClick={() => toggleSet(statusFilter, setStatusFilter)(s)}
                      className={`inline-flex items-center gap-1 h-6 px-2 rounded-sm border font-bold uppercase tracking-wider transition-colors ${active ? statusColor(s) + " ring-1 ring-current/30" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                      {s}
                      <span className="text-[10px] font-mono opacity-70">{meta.count}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {typeKeys.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="my-quotes-type-chips">
                <span className="font-bold uppercase tracking-wider text-slate-500 mr-1">{t("my_prepared_quotes.filter_type", "Type")}</span>
                {typeKeys.map((s) => {
                  const active = typeFilter.has(s);
                  const meta = summary.by_type[s] || { count: 0, total: 0 };
                  return (
                    <button key={s} type="button"
                      data-testid={`my-quotes-type-chip-${s}`}
                      onClick={() => toggleSet(typeFilter, setTypeFilter)(s)}
                      className={`inline-flex items-center gap-1 h-6 px-2 rounded-sm border font-bold uppercase tracking-wider transition-colors ${active ? "bg-indigo-50 text-indigo-700 border-indigo-300 ring-1 ring-indigo-200" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                      {s}
                      <span className="text-[10px] font-mono opacity-70">{meta.count}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8" data-testid="my-quotes-main">
        {err && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3 mb-3" data-testid="my-quotes-error">
            {err}
          </div>
        )}

        {/* Summary cards */}
        {data && total > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="my-quotes-summary">
            <SummaryCard label={t("my_prepared_quotes.summary_total", "Quotes")} value={total.toLocaleString()} sub={t("my_prepared_quotes.summary_filtered", "after filters")} />
            <SummaryCard label={t("my_prepared_quotes.summary_grand_total", "Grand total")} value={fmtMoney(summary.grand_total)} sub={t("my_prepared_quotes.summary_billable", "sum of Shipment Total")} tone="text-emerald-700" />
            <SummaryCard label={t("my_prepared_quotes.summary_open_balance", "Open balance")} value={fmtMoney(summary.open_balance)} sub={t("my_prepared_quotes.summary_unpaid_invoices", "unpaid invoices")} tone="text-rose-700" />
            <SummaryCard label={t("my_prepared_quotes.summary_avg", "Avg ticket")} value={fmtMoney(total > 0 ? summary.grand_total / total : 0)} sub={t("my_prepared_quotes.summary_per_quote", "per quote")} tone="text-slate-700" />
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center h-48" data-testid="my-quotes-loading">
            <Spinner size={28} className="animate-spin text-slate-400" />
          </div>
        ) : !loading && total === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="my-quotes-empty">
            <FileText size={42} weight="duotone" className="text-slate-300 mx-auto mb-3" />
            <div className="text-sm font-bold text-slate-600">
              {hasAnyFilter
                ? t("my_prepared_quotes.empty_filtered", "No quotes match the current filters.")
                : t("my_prepared_quotes.empty_none", "You haven't prepared any quotes yet.")}
            </div>
            {hasAnyFilter && (
              <button onClick={clearAllFilters}
                className="mt-3 inline-flex items-center gap-1 h-7 px-3 rounded-sm border border-slate-300 hover:bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-600">
                <X size={11} weight="bold" /> {t("my_prepared_quotes.clear_filters", "Clear filters")}
              </button>
            )}
          </div>
        ) : (
          <div className="border border-slate-200 rounded-md overflow-x-auto bg-white" data-testid="my-quotes-table">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2">{t("my_prepared_quotes.col_date", "Date")}</th>
                  <th className="text-left px-3 py-2">{t("my_prepared_quotes.col_id", "Quote / Invoice")}</th>
                  <th className="text-left px-3 py-2">{t("my_prepared_quotes.col_company", "Company")}</th>
                  <th className="text-left px-3 py-2">{t("my_prepared_quotes.col_type", "Type")}</th>
                  <th className="text-left px-3 py-2">{t("my_prepared_quotes.col_status", "Status")}</th>
                  <th className="text-right px-3 py-2">{t("my_prepared_quotes.col_total", "Total")}</th>
                  <th className="text-right px-3 py-2">{t("my_prepared_quotes.col_balance", "Balance")}</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((r) => {
                  const ccy = r.currency || "USD";
                  return (
                    <tr key={r.QuoteInvoiceHeadingID}
                      data-testid={`my-quotes-row-${r.QuoteInvoiceHeadingID}`}
                      onClick={() => navigate(`/quote/${encodeURIComponent(r.QuoteInvoiceHeadingID)}`)}
                      className="hover:bg-slate-50 cursor-pointer">
                      <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">
                        {fmtDate(r.invoice_date || r.quote_date)}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px]">
                        {r.invoice_number ? (
                          <span className="text-emerald-700 font-bold">#{r.invoice_number}</span>
                        ) : (
                          <span className="text-slate-700">{r.QuoteInvoiceHeadingID.slice(0, 14)}…</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-semibold text-slate-900 truncate max-w-[260px]" title={r.company_name || r.customer_id || ""}>
                        {r.company_name || r.customer_id || "—"}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-slate-600">{r.Q_Type || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${statusColor(r.Status)}`}>
                          {r.Status || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[11px] text-slate-700">
                        {fmtMoney(r.total, ccy)}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-[11px] font-bold ${r.payment_status === "paid" ? "text-emerald-700" : r.payment_status === "partial" ? "text-amber-700" : r.balance > 0 && r.invoice_number ? "text-rose-700" : "text-slate-400"}`}>
                        {r.invoice_number ? fmtMoney(r.balance, ccy) : "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-300"><ArrowSquareOut size={12} weight="bold" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > perPage && (
          <div className="flex items-center justify-between mt-4 text-[11px] text-slate-600" data-testid="my-quotes-pagination">
            <div>
              {t("my_prepared_quotes.page_label", "Page {page} of {total}", { page, total: totalPages })}
              <span className="mx-2 text-slate-300">•</span>
              {((page - 1) * perPage + 1).toLocaleString()}–{Math.min(page * perPage, total).toLocaleString()} of {total.toLocaleString()}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                data-testid="my-quotes-prev-page"
                className="h-7 px-3 rounded-sm border border-slate-300 hover:bg-slate-100 disabled:opacity-40 font-bold uppercase tracking-wider">
                ← {t("my_prepared_quotes.prev_page", "Prev")}
              </button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                data-testid="my-quotes-next-page"
                className="h-7 px-3 rounded-sm border border-slate-300 hover:bg-slate-100 disabled:opacity-40 font-bold uppercase tracking-wider">
                {t("my_prepared_quotes.next_page", "Next")} →
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function SummaryCard({ label, value, sub, tone = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={`my-quotes-summary-${label}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-lg font-bold font-['Chivo'] mt-0.5 ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}
